import { Key } from "@solid-primitives/keyed"
import { FaSolidTrashCan, FaSolidUserPlus } from 'solid-icons/fa'
import { Accessor, Component, createMemo, DEV, useContext } from "solid-js"
import { Engine } from '../../lib/core/engine/Engine.type'
import type { ColorNamePair } from "../../lib/core/player/colorNamesPool"
import { MAX_PLAYERS } from '../../lib/core/player/consts'
import createMapEditorPlayer from '../../lib/core/player/createMapEditorPlayer'
import { Player } from '../../lib/core/player/Player.type'
import playerDisplayName from "../../lib/core/player/playerDisplayName"
import { createAddPlayerAction } from '../../lib/core/state/flux/action/Map/AddPlayerAction'
import { createDeletePlayerAction } from '../../lib/core/state/flux/action/Map/DeletePlayerAction'
import { createPatchPlayerAction } from '../../lib/core/state/flux/action/Map/PatchPlayerAction'
import dispatchClient from '../../lib/core/state/flux/dispatchClient'
import Unexpected from "../../lib/Exception/Unexpected.class"
import Button from "../../lib/jsx/Button"
import { createOneOrZeroSignal } from '../../rx/signal/create_one_or_zero_signal'
import PlayerColorsBar from "../ArmyPainter/PlayerColorsBar"
import EngineContext from '../EngineContext'
import SelectPlayerColorFromPoolDropDownButton from "./SelectPlayerColorFromPoolDropDownButton"
import SpriteCountTable from './SpriteCountTable'

const PlayersWidget: Component = () => {
  const engine: Engine = useContext(EngineContext)

  const atMaxPlayerCount = createMemo<boolean>(() => {
    return engine.state.players.length >= MAX_PLAYERS
  })

  function addPlayer(): void {
    // console.log('PlayersWidget.addPlayer()')
    if (!atMaxPlayerCount()) {
      dispatchClient(engine, createAddPlayerAction(createMapEditorPlayer(engine.state, engine.state.rng)))
    }
  }

  // const collapsingAnimationMs = 350
  function togglePlayer(player: Player): void {
    // console.log('PlayersWidget.setPlayer()')
    if (engine.selectedPlayerId === player.id) {
      engine.selectedPlayerId = null
    } else {
      engine.selectedPlayerId = player.id
    }
  }

  return <div id="MapEditorPlayersWidget" class="card-body w-20-rem">
    <h5 class="card-title clearfix">
      Players
      <Button class="btn btn-sm btn-secondary float-end" name="add_player" onClick={addPlayer} disabled={atMaxPlayerCount()}
      >
        <FaSolidUserPlus />&nbsp;Add Player
      </Button>
    </h5>

    <div class="accordion" id="playerWidget">
      <Key each={engine.state.players} by="id">
        {(player: Accessor<Player>) => {
          // console.log('player', deepClone(player()))
          const player_id_string = `player_${player()?.id || 'null'}`
          const [isCollapsing] = createOneOrZeroSignal(0)
          const accordionCollapseClass = createMemo<string>(() => {
            // need to call both for tracking to work
            const isCollapsingValue = isCollapsing()
            if (engine.selectedPlayerId === player()?.id) {
              if (isCollapsingValue) {
                return "accordion-collapse collapsing show"
              }
              return "accordion-collapse collapse show"
            } else if (isCollapsingValue) {
              return "accordion-collapse collapsing"
            }
            return "accordion-collapse collapse"
          })

          return <div class="accordion-item">
            <h2 class="accordion-header">
              <button
                class={engine.selectedPlayerId === player()?.id ? "accordion-button" : "accordion-button collapsed"}
                type="button"
                aria-expanded={engine.selectedPlayerId === player()?.id ? "true" : "false"}
                aria-controls={player_id_string}
                onClick={() => { togglePlayer(player()) }}
              >
                <PlayerColorsBar value={player()} width={16} height={16} />&nbsp;
                {playerDisplayName(player()) || 'Unknown'}
              </button>
            </h2>
            <div
              id={player_id_string}
              class={accordionCollapseClass()}
              data-bs-parent={"#playerWidget"}
            >
              <div class="accordion-body">
                {/* <p><span class="small muted">ID:</span> <span class="small font-monospace">{player()?.id}</span></p> */}
                <input value={player()?.dname_placeholder as string | undefined} type="text"
                  onInput={(event: InputEvent) => {
                    dispatchClient(engine, createPatchPlayerAction(player()?.id, {
                      dname_placeholder: (event.target as HTMLInputElement)?.value
                    }))
                  }}
                  placeholder="Display Name" class="form-control" />
                <div>
                  <SelectPlayerColorFromPoolDropDownButton player={player()} onChange={(colorPair: ColorNamePair) => {
                    console.log('onChange', { colorPair })
                    const playerId = player()?.id
                    if (DEV && !playerId) {
                      throw new Unexpected('!playerId')
                    }
                    dispatchClient(engine, createPatchPlayerAction(playerId, {
                      color1: colorPair.color1,
                      color2: colorPair.color2,
                    }))
                  }} />
                </div>
                <SpriteCountTable player={player()} />

                <Button class="btn btn-sm btn-outline-danger" name="del_player" onClick={() => dispatchClient(engine, createDeletePlayerAction(player().id))}>
                  Delete Player&nbsp;<FaSolidTrashCan />
                </Button>
              </div>
            </div>
          </div>
        }}
      </Key>
    </div>
  </div>
}

export default PlayersWidget

